import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { observer } from 'mobx-react';
import accountsStore from 'store/accountsStore';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import Tabs from 'ui/Tabs/Tabs';
import Loader from 'ui/Loader/Loader';
import MobileSubtitle from 'ui/MobileSubtitle/MobileSubtitle';
import { WarningModal } from 'components/modals';
import { IAccount } from 'store/accounts.model';
import api from 'api/api';
import BulkFileUpload from './tabs/BulkTransfer/Subtabs/BulkFileUpload/BulkFileUpload';
import BulkManualEntry from './tabs/BulkTransfer/Subtabs/BulkManualEntry/BulkManualEntry';

import { tabsOptions } from './transfersData';

import BetweenAccounts from './tabs/BetweenAccounts/BetweenAccounts';
import ToFirstAtlantic from './tabs/ToFirstAtlantic/ToFirstAtlantic';
import ToAnotherBank from './tabs/ToAnotherBank/ToAnotherBank';

import { IGetAllowableCurrency } from './Transfers.model';
import styles from './Transfers.module.css';

let innerTabsOptions = [{ label: 'File Upload' }, { label: 'Manual Entry' }];

const Transfers = () => {
  const [activeTab, setActiveTab] = useState<number | false>(0);
  const [activeInnerTab, setActiveInnerTab] = useState<number | false>(0);
  const [nextTab, setNextTab] = useState<number | false>(0);
  const [nextInnerTab, setNextInnerTab] = useState<number | false>(false);
  const [showWarning, setShowWarning] = useState(false);
  const [simpleWarning, setSimpleWarning] = useState<string>();
  const [isFormDirty, setFormDirty] = useState(false);

  const [accountsList, setAccountsList] = useState<IAccount[]>([]);
  const [allCurrencyList, setAllCurrencyList] = useState<string[]>([]);

  const [loadingData, setLoadingData] = useState(true);

  const [bulkTransfersState, setBulkTransfersState] = useState<'adding' | 'confirmation' | 'report'>('adding');
  const openAddBulkTransfersForm = useCallback(() => setBulkTransfersState('adding'), []);

  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  const { accounts: storeAccounts } = accountsStore;
  useEffect(() => {
    setLoadingData(true);

    const fetchallowableCurrency = () =>
      api
        .get('servicereq/currencies', {}, false, false)
        .then((data: IGetAllowableCurrency) => {
          setAllCurrencyList(data.allowableCurrency);
        })
        .catch((e) => console.error(e));

    Promise.all([fetchallowableCurrency()]).finally(() => {
      setLoadingData(false);
    });
  }, []);

  useEffect(() => {
    setAccountsList(storeAccounts.filter(({ currency }) => allCurrencyList.includes(currency)));
  }, [storeAccounts, allCurrencyList]);
  interface IIocationState {
    tab: number | false;
    innerTab: number | false;
    data: Record<string, string>;
  }

  const location = useLocation<IIocationState>();

  useEffect(() => {
    if (downMdScreen) {
      setActiveTab(false);
      setActiveInnerTab(false);
      // TODO: verify this re assignment is necessary.
      innerTabsOptions = [{ label: 'Bulk Transfer: File Upload' }, { label: 'Bulk Transfer: Manual Entry' }];
    } else {
      setActiveTab(nextTab);
      setActiveInnerTab(nextInnerTab);
      // TODO: verify this re assignment is necessary.
      innerTabsOptions = [{ label: 'File Upload' }, { label: 'Manual Entry' }];
    }
    if (location.state) {
      setActiveTab(location.state.tab);
      setActiveInnerTab(location.state.innerTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downMdScreen]);

  useEffect(() => {
    const fetchAccounts = () => {
      if (storeAccounts.length) {
        // accounts loaded already
        return Promise.resolve();
      }
      return accountsStore.fetchAccounts();
    };

    setLoadingData(true);

    Promise.all([fetchAccounts()]).finally(() => {
      setLoadingData(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChangeTab = useCallback(
    (_event: React.ChangeEvent<unknown>, tabIndex: number) => {
      if (tabIndex !== 3 && nextInnerTab !== false) setNextInnerTab(false);
      if (tabIndex === 3 && nextInnerTab === false) setNextInnerTab(0);
      setNextTab(tabIndex);

      if (isFormDirty) {
        setShowWarning(true);
      } else {
        if (tabIndex !== 3 && nextInnerTab !== false) setActiveInnerTab(false);
        if (tabIndex === 3 && nextInnerTab === false) setActiveInnerTab(0);
        setActiveTab(tabIndex);
      }
    },
    [isFormDirty, nextInnerTab],
  );

  const handleOnChangeInnerTab = (_event: React.ChangeEvent<unknown>, tabIndex: number) => {
    if (nextTab !== 3) setNextTab(3);
    setNextInnerTab(tabIndex);

    if (isFormDirty) {
      setShowWarning(true);
    } else {
      if (activeTab !== 3) setActiveTab(3);
      setActiveInnerTab(tabIndex);
    }
  };

  const handleClickBack = () => {
    if (isFormDirty) {
      setShowWarning(true);
    } else {
      setActiveTab(false);
      setActiveInnerTab(false);
    }
  };

  const onLeavePage = () => {
    setShowWarning(false);
    setFormDirty(false);
    if (downMdScreen) {
      setActiveTab(false);
      setActiveInnerTab(false);
    } else {
      setActiveTab(nextTab);
      setActiveInnerTab(nextInnerTab);
    }
  };

  const closeWarning = () => setShowWarning(false);
  const switchDirtyTrue = () => setFormDirty(true);

  const closeSimpleWarning = useCallback(() => setSimpleWarning(undefined), []);

  const actualOptions = downMdScreen && activeTab === false ? tabsOptions.slice(0, 3) : tabsOptions;
  const actualActiveInnerTab = downMdScreen && activeTab !== false ? 0 : activeInnerTab;

  const getTitle = () => {
    if (downMdScreen) {
      if (activeInnerTab !== false) {
        return `Transfers ${innerTabsOptions[activeInnerTab].label.slice(15)}`;
      }
      if (activeTab !== false) {
        return `Transfer ${tabsOptions[activeTab].label}`;
      }
    }
    return 'Transfers';
  };

  const renderTabs = () => {
    if (downMdScreen && activeTab !== false) {
      return null;
    }

    return (
      <>
        <Tabs value={activeTab} options={actualOptions} onChange={handleOnChangeTab} disabled={loadingData} />
        <Tabs
          value={actualActiveInnerTab}
          options={innerTabsOptions}
          onChange={handleOnChangeInnerTab}
          theme="secondary"
          disabled={loadingData}
        />
      </>
    );
  };

  const showMainForm = !loadingData && (activeTab !== 3 || bulkTransfersState === 'adding');
  const showTitle = !downMdScreen || bulkTransfersState !== 'confirmation';

  return (
    <>
      <DashboardCard className={styles.dashCard}>
        {showTitle && (
          <NavigationTitle
            to="#/"
            layoutType={LayoutType.dashboard}
            onClick={handleClickBack}
            noArrow={activeTab === false}
          >
            {getTitle()}
          </NavigationTitle>
        )}
        {downMdScreen && activeTab === false && <MobileSubtitle>Select Transfer Category</MobileSubtitle>}
        {renderTabs()}
        {loadingData && <Loader />}
        {!loadingData && activeTab === 0 && (
          <BetweenAccounts accounts={accountsList} handleChangeForm={switchDirtyTrue} />
        )}
        {!loadingData && activeTab === 1 && (
          <ToFirstAtlantic accounts={accountsList} handleChangeForm={switchDirtyTrue} />
        )}
        {!loadingData && activeTab === 2 && (
          <ToAnotherBank accounts={accountsList} handleChangeForm={switchDirtyTrue} />
        )}
      </DashboardCard>

      {!loadingData && activeInnerTab === 0 && (
        <BulkFileUpload
          accounts={accountsList}
          bulkTransfersState={bulkTransfersState}
          openAddBulkTransfersForm={openAddBulkTransfersForm}
          showMainForm={showMainForm}
          handleChangeForm={switchDirtyTrue}
        />
      )}
      {!loadingData && activeInnerTab === 1 && (
        <BulkManualEntry
          accounts={accountsList}
          bulkTransfersState={bulkTransfersState}
          showMainForm={showMainForm}
          handleChangeForm={switchDirtyTrue}
        />
      )}
      <WarningModal open={showWarning} submitName="Leave Page" onSubmit={onLeavePage} onClose={closeWarning}>
        Entered data will be lost. Are you sure you want to leave page?
      </WarningModal>
      <WarningModal open={!!simpleWarning} onClose={closeSimpleWarning} cancelName="Close">
        {simpleWarning}
      </WarningModal>
    </>
  );
};

export default observer(Transfers);
